export const translations = {
  en: {
    // Navbar
    nav: {
      home: "Home",
      howWeWork: "How We Work",
      process: "Process",
      calculator: "Calculator",
      getStarted: "Get Started",
      aboutUs: "About Us",
      startNow: "Start Now"
    },

    // Hero Section
    hero: {
      titleStart: "",
      titleHighlight: "Custom Automation Solutions",
      subtitle: "Transform your repetitive business processes into efficient automated workflows. We don't sell tools—we deliver smart solutions tailored to your needs.",
      cta: "Discover How We Work",
      ctaSecondary: "Get Started Now"
    },

    // Industry Selector
    industry: {
      title: "What's Your Industry?",
      subtitle: "See how automation transforms businesses like yours",
      painPointsTitle: "Common Repeatable Pains",
      soundFamiliar: "Sound familiar? Let's quantify the pain.",
      industries: {
        marketing: {
          name: "Marketing Agency",
          pains: ["Client Onboarding", "Weekly Reporting", "Lead Qualification", "Content Scheduling", "Campaign Analytics"]
        },
        realestate: {
          name: "Real Estate",
          pains: ["Property Listings", "Client Follow-ups", "Document Management", "Showing Coordination", "Market Reports"]
        },
        ecommerce: {
          name: "E-commerce",
          pains: ["Inventory Management", "Order Processing", "Customer Support", "Product Updates", "Shipping Notifications"]
        },
        consulting: {
          name: "Consulting",
          pains: ["Client Discovery", "Proposal Generation", "Time Tracking", "Invoice Processing", "Research Compilation"]
        },
        healthcare: {
          name: "Healthcare",
          pains: ["Appointment Scheduling", "Patient Reminders", "Record Management", "Insurance Verification", "Billing"]
        },
        legal: {
          name: "Legal Services",
          pains: ["Document Review", "Case Management", "Client Communication", "Billing Hours", "Compliance Tracking"]
        },
        saas: {
          name: "SaaS Company",
          pains: ["User Onboarding", "Support Tickets", "Feature Requests", "Usage Analytics", "Subscription Management"]
        },
        manufacturing: {
          name: "Manufacturing",
          pains: ["Inventory Tracking", "Quality Control", "Production Reports", "Supplier Orders", "Maintenance Logs"]
        }
      }
    },

    // LRP Framework
    lrp: {
      title: "Key Questions We Ask During Discovery",
      subtitle: "Understanding your workflow helps us design the perfect automation solution",
      criticalForAutomation: "✓ Critical for automation design",
      weDoNotSellTools: "We Don't Sell Tools",
      weDeliverSmartSolutions: "We Deliver Smart Solutions"
    },

    // Timeline
    timeline: {
      title: "From Problem to Proof Today",
      subtitle: "Experience our streamlined process that transforms your workflow challenges into working automation solutions",
      clickToLearnMore: "Click to learn more",
      calculateROI: "Calculate Your ROI",
      seeYourSavings: "See your potential savings",
      getFreePOC: "Get Your Free POC",
      noCommitment: "No commitment required",
      discovery: {
        title: "Discovery Call",
        description: "Free 30-minute consultation to understand your needs and identify automation opportunities."
      },
      proposal: {
        title: "Custom Proposal",
        description: "Detailed roadmap with timeline, pricing, and expected ROI for your automation solution."
      },
      development: {
        title: "Development",
        description: "We build your custom automation with regular check-ins and progress updates."
      },
      testing: {
        title: "Testing & Training",
        description: "Thorough testing and comprehensive training to ensure smooth adoption."
      },
      launch: {
        title: "Launch & Support",
        description: "Go live with ongoing support and optimization to maximize your results."
      }
    },

    // ROI Calculator
    calculator: {
      title: "What Is Inefficiency Costing You?",
      subtitle: "Calculate your potential savings with automation",
      roiCalculator: "ROI CALCULATOR",
      yourCurrentSituation: "Your Current Situation",
      hoursPerWeek: "Hours per week:",
      hourlyCost: "Hourly cost:",
      automationPotential: "Automation potential:",
      yourPotentialImpact: "Your Potential Impact",
      currentAnnualCost: "Current Annual Cost",
      returnOnInvestment: "Return on Investment",
      withYourPricing: "(With Your Pricing)",
      monthPayback: "month payback",
      annualSavingsPotential: "Annual Savings Potential",
      perMonth: "per month",
      pricingControls: "Pricing Controls",
      percentOfSavings: "% of Savings",
      fixedPrice: "Fixed Price",
      pricing: "Pricing:",
      ofAnnualSavings: "of annual savings",
      fixedProjectPrice: "Fixed Project Price",
      enterAmount: "Enter amount",
      yourInvestment: "YOUR INVESTMENT",
      fixedPricing: "Fixed pricing",
      clientNetBenefit: "CLIENT NET BENEFIT",
      investmentVsReturn: "Investment vs. Return (First Year)",
      projectInvestment: "Project Investment (Real)",
      oldRoiGeneric: "Old ROI (generic $5k):"
    },

    // CTA Boxes
    cta: {
      title: "Ready to Reclaim Your Time?",
      subtitle: "Choose your path forward",
      poc: {
        badge: "POPULAR CHOICE",
        title: "Discovery Meeting",
        price: "FREE",
        description: "Schedule a video meeting to learn about our work process, understand what a Proof of Concept is, and explore potential free trial opportunities.",
        features: [
          "30-minute video consultation",
          "Learn our proven process",
          "Understand POC benefits",
          "Explore free trial options"
        ],
        button: "Schedule Discovery Meeting"
      },
      guaranteed: {
        badge: "FULL SERVICE",
        title: "Detailed Project Scoping",
        price: "ALL INCLUSIVE",
        description: "Receive a comprehensive analysis of your project requirements through a structured scoping session and discovery meeting to define your complete automation solution.",
        features: [
          "Comprehensive discovery meeting",
          "Return on investment calculations",
          "Custom solution architecture",
          "Complete implementation plan"
        ],
        button: "Schedule Project Scoping"
      },
      question: "Still have questions?",
      talk: "Let's talk"
    },

    // About Us
    about: {
      title: "About Us",
      subtitle: "Automation experts dedicated to your success",
      developer: {
        title: "Expert Developer",
        description: "Web developer with extensive experience helping companies automate their workflows and scale their operations.",
        portfolio: "Portfolio",
        devPortfolio: "Dev Portfolio"
      },
      partner: {
        title: "Our Partner",
        company: "Haru Studio",
        description: "Marketing and publicity brand specializing in digital transformation and business growth strategies."
      }
    },

    // Contact
    contact: {
      tabs: {
        poc: "Discovery Meeting",
        guaranteed: "Guaranteed Project",
        general: "General Inquiry"
      },
      titles: {
        poc: "Schedule Your Discovery Meeting",
        guaranteed: "Scope Your Guaranteed Project",
        general: "Get in Touch"
      },
      descriptions: {
        poc: "Book a 30-minute video consultation to learn about our work process, understand what a Proof of Concept is, and explore potential free trial opportunities.",
        guaranteed: "Tell us about your project and we'll create a detailed scope with complete training and implementation included.",
        general: "Have questions or want to discuss your automation needs? We're here to help."
      },
      form: {
        fullName: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        company: "Company Name",
        industry: "Industry",
        industryPlaceholder: "Select your industry",
        industryOther: "Please specify",
        industryOtherPlaceholder: "Enter your industry",
        bottleneck: "What's your biggest bottleneck or time drain?",
        bottleneckPlaceholder: "Describe the repetitive task or process that's consuming your time...",
        hoursPerWeek: "How many hours per week does this cost you?",
        hoursPerWeekPlaceholder: "e.g., 10-15 hours per week",
        projectDescription: "Project Description",
        projectDescriptionPlaceholder: "Describe your automation needs and goals...",
        budget: "Estimated Budget Range",
        budgetPlaceholder: "Select budget range",
        estimatedCost: "Hourly Cost of Your Workers (USD)",
        estimatedCostPlaceholder: "e.g., 25",
        meetingDateTime: "Preferred Meeting Date & Time",
        budgetOptions: {
          under5k: "Under $5,000",
          range5k15k: "$5,000 - $15,000",
          range15k30k: "$15,000 - $30,000",
          range30k50k: "$30,000 - $50,000",
          over50k: "Over $50,000"
        },
        timeline: "Desired Timeline",
        timelinePlaceholder: "Select timeline",
        timelineOptions: {
          urgent: "ASAP (1-2 weeks)",
          soon: "Soon (1 month)",
          flexible: "Flexible (2-3 months)",
          planning: "Just planning"
        },
        expectedResults: "What results would you like to see?",
        expectedResultsPlaceholder: "e.g., Save 20 hours per week, increase revenue by 30%, reduce errors by 90%...",
        message: "How can we help you?",
        messagePlaceholder: "Tell us about your needs, questions, or challenges...",
        preferredDateTime: "Preferred Meeting Date & Time",
        mainChallenge: "What's your main challenge or goal?",
        mainChallengePlaceholder: "Brief description of your biggest business challenge...",
        currentProcess: "Current Process (Optional)",
        currentProcessPlaceholder: "How do you currently handle this? (Optional)",
        teamSize: "Team Size (for training)",
        teamSizePlaceholder: "Select team size",
        teamSizeOptions: {
          small: "1-5 people",
          medium: "6-20 people",
          large: "21-50 people",
          enterprise: "50+ people"
        },
        required: "*",
        submit: "Submit Request"
      },
      industries: {
        realEstate: "Real Estate",
        marketing: "Marketing",
        consulting: "Consulting",
        ecommerce: "E-commerce",
        healthcare: "Healthcare",
        finance: "Finance",
        technology: "Technology",
        other: "Other"
      },
      budget: {
        under5k: "Under $5,000",
        "5k15k": "$5,000 - $15,000",
        "15k30k": "$15,000 - $30,000",
        "30k50k": "$30,000 - $50,000",
        over50k: "Over $50,000"
      },
      timeline: {
        urgent: "ASAP (1-2 weeks)",
        soon: "Soon (1 month)",
        flexible: "Flexible (2-3 months)",
        planning: "Just planning"
      },
      popup: {
        title: "Request Received!",
        message: "Thank you for reaching out! We'll review your information and get back to you within 24 hours with next steps.",
        details: "Check your email for a confirmation and detailed timeline of what to expect.",
        button: "Back to Home"
      }
    },

    // Footer
    footer: {
      tagline: "Automating repetitive tasks to help businesses focus on what matters most.",
      product: "Product",
      company: "Company",
      legal: "Legal",
      newsletter: {
        title: "Stay Updated",
        description: "Get the latest automation insights and tips delivered to your inbox.",
        placeholder: "Enter your email",
        button: "Subscribe",
        success: "✓ Subscribed!",
        error: "✗ Error",
        confirmation: "Thank you for subscribing! Check your email for confirmation."
      },
      links: {
        features: "Features",
        pricing: "Pricing",
        roiCalculator: "ROI Calculator",
        aboutUs: "About Us",
        ourProcess: "Our Process",
        contact: "Contact",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        cookies: "Cookie Policy",
        gdpr: "GDPR",
        sitemap: "Sitemap",
        accessibility: "Accessibility",
        status: "Status"
      },
      copyright: "Kairos Site. All rights reserved."
    },

    // Legal
    legal: {
      tabs: {
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        cookies: "Cookie Policy",
        gdpr: "GDPR Compliance",
        accessibility: "Accessibility"
      }
    },

    // Diagnostic Questions
    diagnostic: {
      title: "Understanding Your Bottleneck"
    },

    // Sitemap
    sitemap: {
      title: "Sitemap",
      subtitle: "Navigate through all pages and sections of our website",
      mainPages: "Main Pages",
      services: "Services",
      legalPolicies: "Legal & Policies",
      information: "Information"
    },

    // Status
    status: {
      title: "System Status",
      subtitle: "Real-time status and performance metrics",
      allSystems: "All Systems",
      lastUpdated: "Last updated:",
      operational: "Operational",
      degraded: "Degraded Performance",
      down: "Down",
      unknown: "Unknown",
      website: "Website",
      apiServices: "API Services",
      database: "Database",
      cdn: "CDN",
      emailService: "Email Service",
      paymentGateway: "Payment Gateway",
      uptime30Days: "Uptime (30 days)",
      avgResponseTime: "Avg Response Time",
      uptimeHistory: "90-Day Uptime History",
      someDowntime: "Some downtime"
    },

    // Common
    common: {
      learnMore: "Learn More",
      getStarted: "Get Started",
      contactUs: "Contact Us",
      readMore: "Read More",
      close: "Close",
      back: "Back",
      next: "Next",
      previous: "Previous",
      loading: "Loading...",
      error: "Error",
      success: "Success"
    }
  },

  es: {
    // Navbar
    nav: {
      home: "Inicio",
      howWeWork: "Cómo Trabajamos",
      process: "Proceso",
      calculator: "Calculadora",
      getStarted: "Comenzar",
      aboutUs: "Nosotros",
      startNow: "Comenzar Ahora"
    },

    // Hero Section
    hero: {
      titleStart: "",
      titleHighlight: "Soluciones de Automatización Personalizadas",
      subtitle: "Transforma tus procesos empresariales repetitivos en flujos de trabajo automatizados eficientes. No vendemos herramientas—entregamos soluciones inteligentes adaptadas a tus necesidades.",
      cta: "Descubre Cómo Trabajamos",
      ctaSecondary: "Comenzar Ahora"
    },

    // Industry Selector
    industry: {
      title: "¿Cuál es Tu Industria?",
      subtitle: "Descubre cómo la automatización transforma negocios como el tuyo",
      painPointsTitle: "Dolores Comunes Repetibles",
      soundFamiliar: "¿Te suena familiar? Cuantifiquemos el dolor.",
      industries: {
        marketing: {
          name: "Agencia de Marketing",
          pains: ["Integración de Clientes", "Reportes Semanales", "Calificación de Leads", "Programación de Contenido", "Analíticas de Campañas"]
        },
        realestate: {
          name: "Bienes Raíces",
          pains: ["Listados de Propiedades", "Seguimiento de Clientes", "Gestión de Documentos", "Coordinación de Visitas", "Reportes de Mercado"]
        },
        ecommerce: {
          name: "Comercio Electrónico",
          pains: ["Gestión de Inventario", "Procesamiento de Pedidos", "Atención al Cliente", "Actualizaciones de Productos", "Notificaciones de Envío"]
        },
        consulting: {
          name: "Consultoría",
          pains: ["Descubrimiento de Cliente", "Generación de Propuestas", "Seguimiento de Tiempo", "Procesamiento de Facturas", "Compilación de Investigación"]
        },
        healthcare: {
          name: "Salud",
          pains: ["Programación de Citas", "Recordatorios a Pacientes", "Gestión de Registros", "Verificación de Seguros", "Facturación"]
        },
        legal: {
          name: "Servicios Legales",
          pains: ["Revisión de Documentos", "Gestión de Casos", "Comunicación con Clientes", "Horas de Facturación", "Seguimiento de Cumplimiento"]
        },
        saas: {
          name: "Compañía SaaS",
          pains: ["Integración de Usuarios", "Tickets de Soporte", "Solicitudes de Funciones", "Analíticas de Uso", "Gestión de Suscripciones"]
        },
        manufacturing: {
          name: "Manufactura",
          pains: ["Seguimiento de Inventario", "Control de Calidad", "Reportes de Producción", "Órdenes a Proveedores", "Registros de Mantenimiento"]
        }
      }
    },

    // LRP Framework
    lrp: {
      title: "Preguntas Clave Durante el Descubrimiento",
      subtitle: "Entender tu flujo de trabajo nos ayuda a diseñar la solución de automatización perfecta",
      criticalForAutomation: "✓ Crítico para el diseño de automatización",
      weDoNotSellTools: "No Vendemos Herramientas",
      weDeliverSmartSolutions: "Entregamos Soluciones Inteligentes"
    },

    // Timeline
    timeline: {
      title: "Del Problema a la Prueba Hoy",
      subtitle: "Experimenta nuestro proceso simplificado que transforma tus desafíos de flujo de trabajo en soluciones de automatización funcionando",
      clickToLearnMore: "Haz clic para más información",
      calculateROI: "Calcula Tu ROI",
      seeYourSavings: "Ve tus ahorros potenciales",
      getFreePOC: "Obtén Tu POC Gratis",
      noCommitment: "Sin compromiso requerido",
      discovery: {
        title: "Llamada de Descubrimiento",
        description: "Consulta gratuita de 30 minutos para entender tus necesidades e identificar oportunidades de automatización."
      },
      proposal: {
        title: "Propuesta Personalizada",
        description: "Hoja de ruta detallada con cronograma, precios y ROI esperado para tu solución de automatización."
      },
      development: {
        title: "Desarrollo",
        description: "Construimos tu automatización personalizada con revisiones regulares y actualizaciones de progreso."
      },
      testing: {
        title: "Pruebas y Capacitación",
        description: "Pruebas exhaustivas y capacitación completa para asegurar una adopción sin problemas."
      },
      launch: {
        title: "Lanzamiento y Soporte",
        description: "Sal en vivo con soporte continuo y optimización para maximizar tus resultados."
      }
    },

    // ROI Calculator
    calculator: {
      title: "¿Cuánto Te Está Costando la Ineficiencia?",
      subtitle: "Calcula tus ahorros potenciales con automatización",
      roiCalculator: "CALCULADORA ROI",
      yourCurrentSituation: "Tu Situación Actual",
      hoursPerWeek: "Horas por semana:",
      hourlyCost: "Costo por hora:",
      automationPotential: "Potencial de automatización:",
      yourPotentialImpact: "Tu Impacto Potencial",
      currentAnnualCost: "Costo Anual Actual",
      returnOnInvestment: "Retorno de Inversión",
      withYourPricing: "(Con Tu Precio)",
      monthPayback: "meses de recuperación",
      annualSavingsPotential: "Potencial de Ahorros Anuales",
      perMonth: "por mes",
      pricingControls: "Controles de Precios",
      percentOfSavings: "% de Ahorros",
      fixedPrice: "Precio Fijo",
      pricing: "Precio:",
      ofAnnualSavings: "de ahorros anuales",
      fixedProjectPrice: "Precio Fijo del Proyecto",
      enterAmount: "Ingresa cantidad",
      yourInvestment: "TU INVERSIÓN",
      fixedPricing: "Precio fijo",
      clientNetBenefit: "BENEFICIO NETO DEL CLIENTE",
      investmentVsReturn: "Inversión vs. Retorno (Primer Año)",
      projectInvestment: "Inversión del Proyecto (Real)",
      oldRoiGeneric: "ROI anterior (genérico $5k):"
    },

    // CTA Boxes
    cta: {
      title: "¿Listo Para Recuperar Tu Tiempo?",
      subtitle: "Elige tu camino",
      poc: {
        badge: "OPCIÓN POPULAR",
        title: "Reunión de Descubrimiento",
        price: "GRATIS",
        description: "Agenda una videollamada para conocer nuestro proceso de trabajo, entender qué es una Prueba de Concepto y explorar oportunidades de prueba gratuita.",
        features: [
          "Consulta en video de 30 minutos",
          "Conoce nuestro proceso probado",
          "Entiende los beneficios del POC",
          "Explora opciones de prueba gratis"
        ],
        button: "Agendar Reunión de Descubrimiento"
      },
      guaranteed: {
        badge: "SERVICIO COMPLETO",
        title: "Alcance Detallado del Proyecto",
        price: "TODO INCLUIDO",
        description: "Reciba un análisis integral de los requisitos de su proyecto mediante una sesión estructurada de alcance y reunión de descubrimiento para definir su solución de automatización completa.",
        features: [
          "Reunión de descubrimiento integral",
          "Cálculos de retorno de inversión",
          "Arquitectura de solución personalizada",
          "Plan de implementación completo"
        ],
        button: "Agendar Alcance del Proyecto"
      },
      question: "¿Aún tienes preguntas?",
      talk: "Hablemos"
    },

    // About Us
    about: {
      title: "Nosotros",
      subtitle: "Expertos en automatización dedicados a tu éxito",
      developer: {
        title: "Desarrollador Experto",
        description: "Desarrollador web con amplia experiencia ayudando a empresas a automatizar sus flujos de trabajo y escalar sus operaciones.",
        portfolio: "Portafolio",
        devPortfolio: "Portafolio Dev"
      },
      partner: {
        title: "Nuestro Socio",
        company: "Haru Studio",
        description: "Marca de marketing y publicidad especializada en transformación digital y estrategias de crecimiento empresarial."
      }
    },

    // Contact
    contact: {
      tabs: {
        poc: "Reunión Exploratoria",
        guaranteed: "Proyecto Garantizado",
        general: "Consulta General"
      },
      titles: {
        poc: "Agenda Tu Reunión Exploratoria",
        guaranteed: "Define Tu Proyecto Garantizado",
        general: "Ponte en Contacto"
      },
      descriptions: {
        poc: "Reserva una consulta por video de 30 minutos para conocer nuestro proceso de trabajo, entender qué es una Prueba de Concepto y explorar oportunidades de prueba gratuita.",
        guaranteed: "Cuéntanos sobre tu proyecto y crearemos un alcance detallado con capacitación e implementación completa incluida.",
        general: "¿Tienes preguntas o quieres discutir tus necesidades de automatización? Estamos aquí para ayudar."
      },
      form: {
        fullName: "Nombre Completo",
        email: "Correo Electrónico",
        phone: "Número de Teléfono",
        company: "Nombre de la Empresa",
        industry: "Industria",
        industryPlaceholder: "Selecciona tu industria",
        industryOther: "Por favor especifica",
        industryOtherPlaceholder: "Ingresa tu industria",
        bottleneck: "¿Cuál es tu mayor cuello de botella o pérdida de tiempo?",
        bottleneckPlaceholder: "Describe la tarea o proceso repetitivo que consume tu tiempo...",
        hoursPerWeek: "¿Cuántas horas por semana te cuesta esto?",
        hoursPerWeekPlaceholder: "ej., 10-15 horas por semana",
        projectDescription: "Descripción del Proyecto",
        projectDescriptionPlaceholder: "Describe tus necesidades y objetivos de automatización...",
        budget: "Rango de Presupuesto Estimado",
        budgetPlaceholder: "Selecciona rango de presupuesto",
        estimatedCost: "Costo por Hora de Sus Trabajadores (USD)",
        estimatedCostPlaceholder: "ej., 25",
        meetingDateTime: "Fecha y Hora Preferida de Reunión",
        budgetOptions: {
          under5k: "Menos de $5,000",
          range5k15k: "$5,000 - $15,000",
          range15k30k: "$15,000 - $30,000",
          range30k50k: "$30,000 - $50,000",
          over50k: "Más de $50,000"
        },
        timeline: "Cronograma Deseado",
        timelinePlaceholder: "Selecciona cronograma",
        timelineOptions: {
          urgent: "Lo antes posible (1-2 semanas)",
          soon: "Pronto (1 mes)",
          flexible: "Flexible (2-3 meses)",
          planning: "Solo planeando"
        },
        expectedResults: "¿Qué resultados te gustaría ver?",
        expectedResultsPlaceholder: "ej., Ahorrar 20 horas por semana, aumentar ingresos 30%, reducir errores 90%...",
        message: "¿Cómo podemos ayudarte?",
        messagePlaceholder: "Cuéntanos sobre tus necesidades, preguntas o desafíos...",
        preferredDateTime: "Fecha y Hora Preferida para la Reunión",
        mainChallenge: "¿Cuál es tu principal desafío u objetivo?",
        mainChallengePlaceholder: "Breve descripción de tu mayor desafío empresarial...",
        currentProcess: "Proceso Actual (Opcional)",
        currentProcessPlaceholder: "¿Cómo manejas esto actualmente? (Opcional)",
        teamSize: "Tamaño del Equipo (para capacitación)",
        teamSizePlaceholder: "Selecciona tamaño del equipo",
        teamSizeOptions: {
          small: "1-5 personas",
          medium: "6-20 personas",
          large: "21-50 personas",
          enterprise: "50+ personas"
        },
        required: "*",
        submit: "Enviar Solicitud"
      },
      industries: {
        realEstate: "Bienes Raíces",
        marketing: "Marketing",
        consulting: "Consultoría",
        ecommerce: "Comercio Electrónico",
        healthcare: "Salud",
        finance: "Finanzas",
        technology: "Tecnología",
        other: "Otro"
      },
      budget: {
        under5k: "Menos de $5,000",
        "5k15k": "$5,000 - $15,000",
        "15k30k": "$15,000 - $30,000",
        "30k50k": "$30,000 - $50,000",
        over50k: "Más de $50,000"
      },
      timeline: {
        urgent: "Lo antes posible (1-2 semanas)",
        soon: "Pronto (1 mes)",
        flexible: "Flexible (2-3 meses)",
        planning: "Solo planeando"
      },
      popup: {
        title: "¡Solicitud Recibida!",
        message: "¡Gracias por contactarnos! Revisaremos tu información y te responderemos dentro de 24 horas con los próximos pasos.",
        details: "Revisa tu correo para una confirmación y cronograma detallado de qué esperar.",
        button: "Volver al Inicio"
      }
    },

    // Footer
    footer: {
      tagline: "Automatizando tareas repetitivas para ayudar a las empresas a enfocarse en lo que más importa.",
      product: "Producto",
      company: "Empresa",
      legal: "Legal",
      newsletter: {
        title: "Mantente Actualizado",
        description: "Recibe las últimas ideas y consejos de automatización en tu bandeja de entrada.",
        placeholder: "Ingresa tu correo",
        button: "Suscribirse",
        success: "✓ ¡Suscrito!",
        error: "✗ Error",
        confirmation: "¡Gracias por suscribirte! Revisa tu correo para la confirmación."
      },
      links: {
        features: "Características",
        pricing: "Precios",
        roiCalculator: "Calculadora ROI",
        aboutUs: "Nosotros",
        ourProcess: "Nuestro Proceso",
        contact: "Contacto",
        privacy: "Política de Privacidad",
        terms: "Términos de Servicio",
        cookies: "Política de Cookies",
        gdpr: "Cumplimiento GDPR",
        sitemap: "Mapa del Sitio",
        accessibility: "Accesibilidad",
        status: "Estado"
      },
      copyright: "Kairos Site. Todos los derechos reservados."
    },

    // Legal
    legal: {
      tabs: {
        privacy: "Política de Privacidad",
        terms: "Términos de Servicio",
        cookies: "Política de Cookies",
        gdpr: "Cumplimiento GDPR",
        accessibility: "Accesibilidad"
      }
    },

    // Diagnostic Questions
    diagnostic: {
      title: "Entendiendo Tu Cuello de Botella"
    },

    // Sitemap
    sitemap: {
      title: "Mapa del Sitio",
      subtitle: "Navega por todas las páginas y secciones de nuestro sitio web",
      mainPages: "Páginas Principales",
      services: "Servicios",
      legalPolicies: "Legal y Políticas",
      information: "Información"
    },

    // Status
    status: {
      title: "Estado del Sistema",
      subtitle: "Métricas de estado y rendimiento en tiempo real",
      allSystems: "Todos los Sistemas",
      lastUpdated: "Última actualización:",
      operational: "Operacional",
      degraded: "Rendimiento Degradado",
      down: "Fuera de Servicio",
      unknown: "Desconocido",
      website: "Sitio Web",
      apiServices: "Servicios API",
      database: "Base de Datos",
      cdn: "CDN",
      emailService: "Servicio de Correo",
      paymentGateway: "Pasarela de Pago",
      uptime30Days: "Tiempo activo (30 días)",
      avgResponseTime: "Tiempo de Respuesta Promedio",
      uptimeHistory: "Historial de Tiempo Activo de 90 Días",
      someDowntime: "Algo de inactividad"
    },

    // Common
    common: {
      learnMore: "Saber Más",
      getStarted: "Comenzar",
      contactUs: "Contáctanos",
      readMore: "Leer Más",
      close: "Cerrar",
      back: "Atrás",
      next: "Siguiente",
      previous: "Anterior",
      loading: "Cargando...",
      error: "Error",
      success: "Éxito"
    }
  }
};

export type Language = 'en' | 'es';
export type TranslationKey = typeof translations.en;
